import type * as React from 'react';
import type { Ref } from 'react';
import type { icModel } from 'generated-ts-common';

/**
 * Helper function to check if a given string is empty or not. A string is considered 'empty' if it is either:<br />
 * - 'null'
 * - 'undefined'
 * - A zero-length string and the allowEmptyString parameter is set to 'false'
 *
 * The `value is undefined` return type is special typescript syntax for a custom type guard.
 * If using this inside a condition, it will auto-cast the value you pass in both branches.
 *
 * @param value the value to check
 * @param allowEmptyString if the given string is allowed to have a 0 length or not
 */
export const isEmptyString = (
  value: string | undefined,
  allowEmptyString = false
): value is undefined => {
  return !value || (!allowEmptyString && value.length === 0);
};

/**
 * Helper function to check if a given array is empty or not. An array is considered 'empty' if it is either:<br />
 * - 'null'
 * - 'undefined'
 * - A zero-length array
 *
 * The `value is undefined` return type is special typescript syntax for a custom type guard.
 * If using this inside a condition, it will auto-cast the value you pass in both branches.
 *
 * @typeParam T the type of the array
 * @param value the array to check
 */
export const isEmptyArray = function <T>(
  value: Array<T> | undefined
): value is undefined {
  return !value || value.length === 0;
};

/**
 * Check whether the array is semantically empty, notably including only `undefined` values.
 *
 *
 * @param arr
 */
export function isSemanticallyEmptyArray(arr: Array<unknown>): boolean {
  return isEmptyArray(arr) || arr.filter(v => v !== undefined).length === 0;
}

/**
 * Helper function to merge an array of refs.
 * @param refs the refs to merge together
 * @return a function for merging the refs
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mergeRefs<T>(refs: any[]): Ref<T> {
  return (value): void => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref != null) {
        ref.current = value;
      }
    });
  };
}

/**
 * Helper function to check if a React node is of a component type.
 * React nodes have a `type` property that's set to the constructor, or the function that created the node,
 * which this function makes use of.
 *
 * @param node the node to check
 * @param type the expected type of the node
 * @return `true` if node matches the given type, else `false`
 */
export function reactNodeIsOfType(
  node: React.ReactElement,
  type: React.ReactNode
): boolean {
  return typeof node === 'object' && node !== null && node.type === type;
}

/** Alias representing uuids to make code more readable. */
export type UUID = string;
/**
 * Generate a UUID.
 *
 * These are not cryptographically safe, but good enough for now.
 * If necessary, using a better randomness source would fix this.
 *
 * @return the UUID
 */
export function uuidv4(): UUID {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Checks if the given formatCode is equivalent to one of the given dicomFormatCodeList values or
 * if the given mimeType is `application/dicom`.
 *
 * This method is equivalent to the `Sense.util.ViewerContextUtil#checkResponsibility` method in our ExtJS code.
 *
 * @param formatCode the formatCode to search in the dicomFormatCodeList for
 * @param mimeType the mimeType to check
 * @param dicomFormatCodeList the list of codes that are supposed to be treated as dicoms
 * @return true if supposed to be treated as a dicom
 */
export function isDicomFormat(
  formatCode: icModel.saf.infrastructure.model.svs.GenCode | undefined,
  mimeType: string | undefined,
  dicomFormatCodeList: icModel.saf.infrastructure.model.svs.GenCode[]
): boolean {
  if (mimeType === 'application/dicom') return true;

  return dicomFormatCodeList.some(
    code =>
      code.value === formatCode?.value &&
      (!code.codingScheme || code.codingScheme === formatCode.codingScheme)
  );
}

/**
 * Retry something until it succeeds, or wait for a condition before calling a function, using the exponential backoff algorithm.
 *
 * The function is `5^i, i<=6` , so
 * 5, 25, 125, 625, 3125, 15625 ms
 *
 * @param until fn that returns true when it succeeded/the condition for the callback is met.
 * @param successCallback fn to be called when `until` returns true.
 * @param errorMessage optional error message to log if the condition is never true after the last iteration
 * @param iteration internal parameter used for the recursive algorithm.
 */
export function exponentialBackoff(
  until: () => boolean,
  successCallback: (() => void) | undefined,
  errorMessage: string | undefined,
  iteration?: number | undefined
): void {
  if (until()) {
    successCallback?.();
    return;
  }

  const iter = iteration || 1;

  if (iter <= 6) {
    setTimeout(() => {
      exponentialBackoff(until, successCallback, errorMessage, iter + 1);
    }, Math.pow(5, iter));
  } else if (errorMessage) {
    console.error(errorMessage);
  }
}

/**
 * Converts base64 data to a blob.
 *
 * Copied from ReportPanelController.js extjs code.
 *
 *
 * @param base64data the base64 String data
 * @param contentType the content type of the data
 * @param sliceSize byte slice size (default 512)
 */
export function base64ToBlob(
  base64data: string,
  contentType = '',
  sliceSize = 512
): Blob {
  const byteCharacters = atob(base64data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
}

/**
 *
 * Copy some string data to the user's clipboard
 *
 * @param text string to be copied into user`s clipboard
 */
export const copyToClipboard = (text: string): void => {
  navigator?.clipboard?.writeText(text);
};

/**
 * Checks if the dark theme is currently active.
 */
export const isDarkTheme = (): boolean => {
  return SenseConstants.colorSettings === 'dark';
};

/**
 *
 * Check if array is not empty otherwise it returns undefined
 *
 * @param arr
 */
export function getValidArrayOrUndefined<T>(arr?: T[]): T[] | undefined {
  return arr && arr.length > 0 ? arr : undefined;
}

/**
 * Determines the day index where Monday is the first day of the week.
 *
 * @param date the date to check
 */
export const getDayIndexStartedFromMonday = (date: Date): number => {
  return date.getDay() === 0 ? 6 : date.getDay() - 1;
};
